/**
 * Below this, the sale page is its mobile self.
 *
 * `md` in the v2 config, and not an arbitrary phone width: it is the one boundary this
 * page already changes at. The header swaps to the mobile bar there, the pill navbar
 * hides there (`hidden md:block`) and the categories move into the hamburger. Anything
 * that has to ask "is this the mobile page" is asking about that same switch, so it asks
 * about it in the same terms rather than picking a second number that could disagree.
 */
export const MOBILE_MAX_WIDTH = 767;

/**
 * Whether this is the mobile page, right now.
 *
 * A function rather than a hook because the callers want the answer at the moment of a
 * click — choosing a search suggestion — and not as rendered state. Nothing re-renders
 * when it changes, so subscribing to it would be a re-render of the whole sale for a
 * value nobody is drawing.
 */
export function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

/**
 * What the sale's own containers wear: boxed on a desktop, fluid below `md`.
 *
 * Tailwind derives `.container`'s max-width from the breakpoint it is in, and this
 * config's small breakpoints are narrow — 375px from `xs` and 414px from `sm`. Since the
 * mobile page runs all the way to 767px, every screen between 414 and 767 was drawing a
 * 414px column of products in the middle of it, and the phones just above each
 * breakpoint (390, 402, 412, 430) were each losing their last few pixels to the one
 * below. On a single-column layout those pixels are the ones the Add to Cart button was
 * short of.
 *
 * `max-w-none` rather than dropping `container`, because `container` also carries the
 * centring; and it wins on layer order — Tailwind emits components before utilities, so
 * the utility overrides the media query the component set.
 *
 * Same boundary as `MOBILE_MAX_WIDTH` above, and for the same reason: this is the width
 * at which the page becomes its mobile self, not a second number to keep in step.
 */
export const SALE_CONTAINER = 'max-md:max-w-none';
